import React, { useMemo, useRef, useState } from 'react';
import Card from './Card';
import Button from './Button';
import { inputStyle } from './FormField';
import { sizeKind, roleOf, isAnchor, applyAnchor } from '../utils/priceRules';

const BORDER = '1px solid var(--color-border, #e2e8f0)';
const DIVIDER = '1px solid var(--color-border-strong, #94a3b8)';
const MUTED = { color: 'var(--color-text-muted)', fontSize: 13 };

const keyOf = (size, d) => size.key + '|' + d.name;
const blank = (v) => v === undefined || v === null || String(v).trim() === '';
const tidy = (v) => String(v || '').replace(/\s+/g, ' ').trim();

function groupBySeries(designs) {
  const out = [];
  const idx = new Map();
  (designs || []).forEach((d) => {
    if (!idx.has(d.series)) {
      idx.set(d.series, out.length);
      out.push({ series: d.series, designs: [] });
    }
    out[idx.get(d.series)].designs.push(d);
  });
  return out;
}

const dmy = (s) => (s && String(s).length >= 10 ? String(s).slice(8, 10) + '/' + String(s).slice(5, 7) + '/' + String(s).slice(0, 4) : '');

const sizeKeyOf = (txt) => {
  const m = String(txt || '').match(/(\d{3,4})\s*x\s*(\d{3,4})/i);
  return m ? m[1] + 'X' + m[2] : '';
};

/** Series (and their designs) exactly like the example price list. Sizes without an example use the catalog series. */
function buildSeries(size, sample) {
  const pool = size.designs.map((d) => d.name);
  const sec = (sample || []).find((x) => sizeKeyOf(x.heading) === size.key);
  if (!sec) return { size, pool, series: groupBySeries(size.designs) };
  const seen = new Set();
  const series = [];
  (sec.groups || []).forEach((g) => {
    const list = [];
    (g.designs || []).forEach((n) => {
      const name = tidy(n);
      const up = name.toUpperCase();
      if (!name || seen.has(up)) return;
      seen.add(up);
      list.push({ name, series: g.series_name });
    });
    if (list.length) series.push({ series: g.series_name, designs: list });
  });
  return { size, pool, series };
}

export default function CustomizePriceList({ catalog, sample, history, fetchList, onCreate, onClose }) {
  const sizes = useMemo(() => (Array.isArray(catalog) ? catalog : []), [catalog]);
  const grouped = useMemo(() => sizes.map((s) => buildSeries(s, sample)), [sizes, sample]);

  const [customer, setCustomer] = useState({ name: '', mobile: '', city: '' });
  const [grades, setGrades] = useState({ pre: true, std: true });
  const [picked, setPicked] = useState({}); // "size|design" -> true
  const [prices, setPrices] = useState({}); // "size|series" -> { p1, p2 }
  const [extraDesigns, setExtraDesigns] = useState({}); // "size|series" -> [names typed by the user]
  const [extraSeries, setExtraSeries] = useState({}); // size key -> [series names added by the user]
  const [draft, setDraft] = useState({}); // text boxes
  const [closed, setClosed] = useState({}); // size key -> true when collapsed
  const [q, setQ] = useState('');
  const [err, setErr] = useState('');
  const [hints, setHints] = useState({}); // "size|series" -> last prices given to this customer
  const [loadInfo, setLoadInfo] = useState('');
  const loadedFor = useRef('');
  const viewRef = useRef([]);
  const manual = useRef({}); // size key -> Set of manually edited price cells (same rule as the example sheet)

  const small = { ...inputStyle, width: 90, padding: '4px 6px' };
  const needle = q.trim().toLowerCase();

  /** Example series + whatever the user added, per size. */
  const view = grouped.map((g) => {
    const dk = (name) => g.size.key + '|' + name;
    const withAdded = (name, base, isNew) => ({
      series: name,
      isNew,
      designs: base.concat((extraDesigns[dk(name)] || []).map((n) => ({ name: n, series: name, added: true }))),
    });
    const series = g.series.map((sr) => withAdded(sr.series, sr.designs, false))
      .concat((extraSeries[g.size.key] || []).map((name) => withAdded(name, [], true)));
    return { size: g.size, pool: g.pool, series };
  });

  viewRef.current = view;

  const setPrice = (k, patch) => setPrices((prev) => ({ ...prev, [k]: { ...(prev[k] || {}), ...patch } }));
  const setDraftVal = (k, v) => setDraft((prev) => ({ ...prev, [k]: v }));

  /** Same auto-rate rule as "New from example": the anchor series PRE rate fills every other series of that size. */
  const onPrice = (v, sr, field, value) => {
    const key = v.size.key;
    const pk = key + '|' + sr.series;
    const kind = sizeKind(v.size.heading);
    if (!manual.current[key]) manual.current[key] = new Set();
    const set = manual.current[key];
    const role = roleOf(kind, sr.series);
    if (field === 'p1' && isAnchor(kind, role)) {
      const groups = v.series.map((s) => {
        const p = prices[key + '|' + s.series] || {};
        return { series_name: s.series, designs: [], price1: p.p1 === undefined ? '' : p.p1, price2: p.p2 === undefined ? '' : p.p2 };
      });
      const gi = v.series.findIndex((s) => s.series === sr.series);
      const next = applyAnchor({ heading: v.size.heading, groups }, gi, value, set);
      if (next) {
        setPrices((prev) => {
          const n = { ...prev };
          next.forEach((g, i) => {
            n[key + '|' + v.series[i].series] = {
              p1: g.price1 === null || g.price1 === undefined ? '' : g.price1,
              p2: g.price2 === null || g.price2 === undefined ? '' : g.price2,
            };
          });
          return n;
        });
        return;
      }
      setPrice(pk, { p1: value });
      return;
    }
    if (role) set.add(role + ':' + field);
    setPrice(pk, { [field]: value });
  };

  const lookup = (v, nm) => {
    for (let i = 0; i < v.series.length; i += 1) {
      const d = v.series[i].designs.find((x) => x.name.toUpperCase() === nm);
      if (d) return { d, series: v.series[i].series };
    }
    return null;
  };

  /** Customer already got a price list before: tick the same designs again (size-wise). */
  const loadLast = async (rawName) => {
    const name = tidy(rawName).toLowerCase();
    if (!name || loadedFor.current === name || !fetchList) return;
    const hit = (history || []).find((h) => tidy(h.customer_name).toLowerCase() === name);
    if (!hit) return;
    loadedFor.current = name;
    try {
      const d = await fetchList(hit.price_list_id);
      const cur = viewRef.current;
      const nextPicked = {};
      const addD = {};
      const addS = {};
      const nextHints = {};
      const queued = new Set();
      let any1 = false;
      let any2 = false;
      let n = 0;
      (d.sections || []).forEach((sec) => {
        const v = cur.find((x) => x.size.key === sizeKeyOf(sec.heading));
        if (!v) return;
        (sec.groups || []).forEach((g) => {
          if (g.price1 !== null && g.price1 !== undefined && g.price1 !== '') any1 = true;
          if (g.price2 !== null && g.price2 !== undefined && g.price2 !== '') any2 = true;
          (g.designs || []).forEach((dn) => {
            const nm = tidy(dn).toUpperCase();
            if (!nm) return;
            const hitDesign = lookup(v, nm);
            let seriesName;
            if (hitDesign) {
              nextPicked[keyOf(v.size, hitDesign.d)] = true;
              seriesName = hitDesign.series;
            } else {
              const have = v.series.find((s) => s.series.toUpperCase() === tidy(g.series_name).toUpperCase());
              seriesName = have ? have.series : tidy(g.series_name);
              if (!have && !queued.has(v.size.key + '|S|' + seriesName.toUpperCase())) {
                queued.add(v.size.key + '|S|' + seriesName.toUpperCase());
                addS[v.size.key] = (addS[v.size.key] || []).concat(seriesName);
              }
              if (!queued.has(v.size.key + '|D|' + nm)) {
                queued.add(v.size.key + '|D|' + nm);
                const pk = v.size.key + '|' + seriesName;
                addD[pk] = (addD[pk] || []).concat(nm);
                nextPicked[v.size.key + '|' + nm] = true;
              }
            }
            n += 1;
            nextHints[v.size.key + '|' + seriesName] = { p1: g.price1, p2: g.price2 };
          });
        });
      });
      setPicked((prev) => ({ ...prev, ...nextPicked }));
      setExtraDesigns((prev) => {
        const out = { ...prev };
        Object.keys(addD).forEach((k) => { out[k] = (out[k] || []).concat(addD[k].filter((x) => !(out[k] || []).includes(x))); });
        return out;
      });
      setExtraSeries((prev) => {
        const out = { ...prev };
        Object.keys(addS).forEach((k) => { out[k] = (out[k] || []).concat(addS[k].filter((x) => !(out[k] || []).includes(x))); });
        return out;
      });
      setHints((prev) => ({ ...prev, ...nextHints }));
      if (any1 || any2) setGrades({ pre: any1, std: any2 });
      setCustomer((c) => ({ ...c, mobile: c.mobile || d.customer_mobile || '', city: c.city || d.customer_city || '' }));
      setLoadInfo('Last price list (' + dmy(hit.list_date) + ') ma aapel ' + n + ' design select thai gai. Have price nakho, ke "Last price bharo" dabavo.');
    } catch (e) {
      loadedFor.current = '';
    }
  };

  const useLastPrices = () => {
    setPrices((prev) => {
      const out = { ...prev };
      Object.keys(hints).forEach((k) => {
        const h = hints[k];
        const cur = prev[k] || {};
        out[k] = {
          p1: blank(cur.p1) && h.p1 !== null && h.p1 !== undefined ? h.p1 : cur.p1,
          p2: blank(cur.p2) && h.p2 !== null && h.p2 !== undefined ? h.p2 : cur.p2,
        };
      });
      return out;
    });
  };

  const toggleGrade = (name) => {
    const next = { ...grades, [name]: !grades[name] };
    if (!next.pre && !next.std) return; // at least one grade must stay ticked
    setGrades(next);
  };

  const toggleDesign = (k, on) => setPicked((prev) => ({ ...prev, [k]: on }));

  const toggleSeries = (size, sr, on) => {
    setPicked((prev) => {
      const next = { ...prev };
      sr.designs.forEach((d) => { next[keyOf(size, d)] = on; });
      return next;
    });
  };

  const findDesign = (v, name) => {
    const up = name.toUpperCase();
    for (let i = 0; i < v.series.length; i += 1) {
      if (v.series[i].designs.some((d) => d.name.toUpperCase() === up)) return v.series[i].series;
    }
    return '';
  };

  const addDesign = (v, sr) => {
    const dk = 'd|' + v.size.key + '|' + sr.series;
    const name = tidy(draft[dk]).toUpperCase();
    if (!name) return;
    const where = findDesign(v, name);
    if (where) { setErr(name + ' pehla thi "' + where + '" ma chhe (' + v.size.name + ').'); return; }
    setErr('');
    const pk = v.size.key + '|' + sr.series;
    setExtraDesigns((prev) => ({ ...prev, [pk]: (prev[pk] || []).concat(name) }));
    setPicked((prev) => ({ ...prev, [v.size.key + '|' + name]: true }));
    setDraftVal(dk, '');
  };

  const removeAddedDesign = (v, sr, name) => {
    const pk = v.size.key + '|' + sr.series;
    setExtraDesigns((prev) => ({ ...prev, [pk]: (prev[pk] || []).filter((n) => n !== name) }));
    setPicked((prev) => ({ ...prev, [v.size.key + '|' + name]: false }));
  };

  const addSeries = (v) => {
    const dk = 's|' + v.size.key;
    const name = tidy(draft[dk]);
    if (!name) return;
    if (v.series.some((s) => s.series.toUpperCase() === name.toUpperCase())) { setErr('"' + name + '" series pehla thi chhe (' + v.size.name + ').'); return; }
    setErr('');
    setExtraSeries((prev) => ({ ...prev, [v.size.key]: (prev[v.size.key] || []).concat(name) }));
    setDraftVal(dk, '');
  };

  const removeSeries = (v, sr) => {
    setExtraSeries((prev) => ({ ...prev, [v.size.key]: (prev[v.size.key] || []).filter((n) => n !== sr.series) }));
    setPicked((prev) => {
      const next = { ...prev };
      sr.designs.forEach((d) => { next[keyOf(v.size, d)] = false; });
      return next;
    });
  };

  const countIn = (v) => v.series.reduce((n, sr) => n + sr.designs.filter((d) => picked[keyOf(v.size, d)]).length, 0);
  const totalIn = (v) => v.series.reduce((n, sr) => n + sr.designs.length, 0);
  const total = view.reduce((n, v) => n + countIn(v), 0);

  const create = () => {
    setErr('');
    if (!customer.name.trim()) { setErr('Customer nu nam lakho.'); return; }
    const sections = [];
    const missing = [];
    let count = 0;
    view.forEach((v) => {
      const groups = [];
      v.series.forEach((sr) => {
        const names = sr.designs.filter((d) => picked[keyOf(v.size, d)]).map((d) => d.name);
        if (!names.length) return;
        count += names.length;
        const p = prices[v.size.key + '|' + sr.series] || {};
        const p1 = grades.pre && !blank(p.p1) ? String(p.p1) : '';
        const p2 = grades.std && !blank(p.p2) ? String(p.p2) : '';
        if ((grades.pre && p1 === '') || (grades.std && p2 === '')) missing.push(v.size.name + ' - ' + sr.series);
        groups.push({ series_name: sr.series, designs: names, price1: p1, price2: p2 });
      });
      if (groups.length) {
        sections.push({
          heading: v.size.heading,
          packing: v.size.packing || '',
          box_weight: v.size.box_weight || '',
          footer_label: v.size.footer_label || '',
          groups,
        });
      }
    });
    if (!count) { setErr('Ochha ma ochhi ek design select karo.'); return; }
    if (missing.length) {
      setErr('Aa series nu price nakhvanu baki chhe: ' + missing.slice(0, 5).join(', ') + (missing.length > 5 ? ' ... (+' + (missing.length - 5) + ' vadhu)' : ''));
      return;
    }
    onCreate({
      is_customer: true,
      customer_name: customer.name.trim(),
      customer_mobile: customer.mobile.trim(),
      customer_city: customer.city.trim(),
      col1_label: 'PREM',
      col2_label: 'STD',
      sections,
    });
  };

  return (
    <Card title="Customize Price List">
      <p style={{ margin: '0 0 12px', ...MUTED }}>
        Customer nu nam lakho (junu customer hoy to last ni design automatic select thai jashe), grade (PRE / STD) tick karo, series nu price nakho (anchor series nu PRE price nakhta bija series na rate automatic aavi jashe, example ni jem) ane je design aapvani hoy te tick karo. Koi pan series ma design nu nam lakhi ne add kari shako chho, ane darek size ma navi series pan add kari shako chho. Tick nahi karel grade price list ma dekhase nahi.
      </p>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 240px', fontSize: 13 }}>
          Customer name *
          <input style={{ ...inputStyle, width: '100%' }} value={customer.name} placeholder="Customer nu nam" list="cpl-customers"
            onChange={(e) => { setCustomer({ ...customer, name: e.target.value }); loadLast(e.target.value); }}
            onBlur={(e) => loadLast(e.target.value)} />
          <datalist id="cpl-customers">
            {Array.from(new Set((history || []).map((h) => tidy(h.customer_name)).filter(Boolean))).map((n) => <option key={n} value={n} />)}
          </datalist>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '0 1 160px', fontSize: 13 }}>
          Mobile (optional)
          <input style={{ ...inputStyle, width: '100%' }} value={customer.mobile}
            onChange={(e) => setCustomer({ ...customer, mobile: e.target.value })} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '0 1 160px', fontSize: 13 }}>
          City (optional)
          <input style={{ ...inputStyle, width: '100%' }} value={customer.city}
            onChange={(e) => setCustomer({ ...customer, city: e.target.value })} />
        </label>
      </div>

      {loadInfo && (
        <div style={{ margin: '0 0 12px', padding: '8px 12px', borderRadius: 8, background: 'rgba(34,197,94,0.12)', fontSize: 13, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ flex: 1 }}>{loadInfo}</span>
          <Button variant="secondary" onClick={useLastPrices}>Last price bharo</Button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        <strong style={{ fontSize: 14 }}>Show grade:</strong>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
          <input type="checkbox" checked={grades.pre} onChange={() => toggleGrade('pre')} /> PRE
        </label>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
          <input type="checkbox" checked={grades.std} onChange={() => toggleGrade('std')} /> STD
        </label>
        <input style={{ ...inputStyle, flex: '1 1 200px', maxWidth: 320 }} value={q} placeholder="Search design..."
          onChange={(e) => setQ(e.target.value)} />
        <span style={MUTED}>{total} design selected</span>
        {total > 0 && <Button variant="text" onClick={() => setPicked({})}>Clear selection</Button>}
      </div>

      {view.length === 0 && <p style={MUTED}>Design list khali chhe.</p>}

      {view.map((v) => {
        const shown = v.series.filter((sr) => sr.isNew || sr.designs.length === 0 || !needle
          || sr.series.toLowerCase().includes(needle) || sr.designs.some((d) => d.name.toLowerCase().includes(needle)));
        return (
          <div key={v.size.key} style={{ border: BORDER, borderRadius: 10, marginBottom: 12, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'var(--color-surface-alt, rgba(100,116,139,0.08))' }}>
              <strong style={{ flex: 1 }}>{v.size.heading} <span style={MUTED}>({countIn(v)}/{totalIn(v)} selected)</span></strong>
              <Button variant="text" onClick={() => setClosed({ ...closed, [v.size.key]: !closed[v.size.key] })}>
                {closed[v.size.key] ? 'Show' : 'Hide'}
              </Button>
            </div>
            {!closed[v.size.key] && (
              <div style={{ padding: 12 }}>
                <datalist id={'dl-' + v.size.key}>
                  {v.pool.map((n) => <option key={n} value={n} />)}
                </datalist>
                {shown.length === 0 && <p style={MUTED}>Aa size ma series nathi. Niche thi navi series add karo.</p>}
                {shown.map((sr, si) => {
                  const visible = sr.designs.filter((d) => !needle || d.name.toLowerCase().includes(needle) || sr.series.toLowerCase().includes(needle));
                  const allOn = sr.designs.length > 0 && sr.designs.every((d) => picked[keyOf(v.size, d)]);
                  const pk = v.size.key + '|' + sr.series;
                  const p = prices[pk] || {};
                  const dk = 'd|' + v.size.key + '|' + sr.series;
                  return (
                    <div key={sr.series} style={{ borderTop: si > 0 ? DIVIDER : 'none', marginTop: si > 0 ? 14 : 0, paddingTop: si > 0 ? 14 : 0 }}>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
                        <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer', fontWeight: 600 }}>
                          <input type="checkbox" checked={allOn} onChange={(e) => toggleSeries(v.size, sr, e.target.checked)} />
                          {sr.series} <span style={{ ...MUTED, fontWeight: 400 }}>({sr.designs.length})</span>
                        </label>
                        {sr.isNew && <Button variant="text" onClick={() => removeSeries(v, sr)}>Remove series</Button>}
                        <span style={{ flex: 1 }} />
                        {grades.pre && <input style={small} type="number" min="0" step="0.5" placeholder={hints[pk] && hints[pk].p1 ? 'Last ' + hints[pk].p1 : 'PRE price'} value={p.p1 === undefined ? '' : p.p1} onChange={(e) => onPrice(v, sr, 'p1', e.target.value)} />}
                        {grades.std && <input style={small} type="number" min="0" step="0.5" placeholder={hints[pk] && hints[pk].p2 ? 'Last ' + hints[pk].p2 : 'STD price'} value={p.p2 === undefined ? '' : p.p2} onChange={(e) => onPrice(v, sr, 'p2', e.target.value)} />}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 6 }}>
                        {visible.map((d) => {
                          const k = keyOf(v.size, d);
                          return (
                            <label key={k} style={{ display: 'flex', gap: 6, alignItems: 'center', border: BORDER, borderRadius: 8, padding: '6px 8px', cursor: 'pointer', fontSize: 13, opacity: picked[k] ? 1 : 0.8 }}>
                              <input type="checkbox" checked={!!picked[k]} onChange={(e) => toggleDesign(k, e.target.checked)} />
                              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={d.name}>{d.name}</span>
                              {d.added && (
                                <button type="button" title="Remove design" onClick={(e) => { e.preventDefault(); removeAddedDesign(v, sr, d.name); }}
                                  style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 0, color: 'var(--color-text-muted)' }}>&times;</button>
                              )}
                            </label>
                          );
                        })}
                      </div>
                      <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                        <input style={{ ...inputStyle, flex: '1 1 220px', maxWidth: 320, padding: '4px 8px' }} list={'dl-' + v.size.key}
                          value={draft[dk] || ''} placeholder={'+ Design add karo (' + sr.series + ')'}
                          onChange={(e) => setDraftVal(dk, e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addDesign(v, sr); } }} />
                        <Button variant="secondary" onClick={() => addDesign(v, sr)}>Add design</Button>
                      </div>
                    </div>
                  );
                })}
                <div style={{ display: 'flex', gap: 8, marginTop: 16, paddingTop: 14, borderTop: DIVIDER, flexWrap: 'wrap' }}>
                  <input style={{ ...inputStyle, flex: '1 1 220px', maxWidth: 320, padding: '4px 8px' }}
                    value={draft['s|' + v.size.key] || ''} placeholder="+ Navi series nu nam"
                    onChange={(e) => setDraftVal('s|' + v.size.key, e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSeries(v); } }} />
                  <Button variant="secondary" onClick={() => addSeries(v)}>Add series</Button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {err && <p style={{ color: '#b91c1c', margin: '8px 0' }}>{err}</p>}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <Button onClick={create}>Create customer price list</Button>
        <Button variant="secondary" onClick={onClose}>Close</Button>
      </div>
    </Card>
  );
}
